const BOOKING_STATUS_LABELS = {
  draft: "Draft",
  submitted: "Submitted",
  pending: "Under review",
  pending_operations_approval: "Under review",
  under_review: "Under review",
  assigned: "Assigned",
  confirmed: "Confirmed",
  on_hold: "On hold",
  rejected: "Rejected",
  cancelled: "Cancelled",
  canceled: "Cancelled",
  completed: "Completed",
  confirm: "Confirmed",
  payment_pending: "Payment pending",
  partially_paid: "Partially paid",
  documents_pending: "Documents pending",
  documents_issued: "Documents issued",
  expired: "Expired",
  refund_requested: "Refund requested",
  refunded: "Refunded",
};

const PAYMENT_STATUS_LABELS = {
  pending: "Payment pending",
  pending_verification: "Pending review",
  submitted: "Pending review",
  partially_paid: "Partially paid",
  paid: "Paid",
  received: "Received",
  approved: "Received",
  overdue: "Overdue",
  refund_pending: "Refund pending",
  refunded: "Refunded",
  failed: "Failed",
  rejected: "Rejected",
};

const AGENT_CANCELLABLE = ["draft", "submitted", "pending", "assigned"];
const AGENT_EDITABLE_PASSENGERS = ["draft", "submitted", "pending"];
const CONFIRMED_STATUSES = ["confirmed", "confirm"];

export function normalizeBookingStatus(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
}

export function formatBookingStatus(value) {
  const normalized = normalizeBookingStatus(value);
  if (BOOKING_STATUS_LABELS[normalized]) {
    return BOOKING_STATUS_LABELS[normalized];
  }
  if (!normalized) {
    return "Unknown";
  }
  return normalized.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function storedBookingStatus(booking = {}) {
  return booking.upbu_booking_status || booking.booking_status || "";
}

export function travelServiceStatus(name, status) {
  const normalized = normalizeBookingStatus(status);
  if (["confirmed", "published", "completed"].includes(normalized)) {
    return normalized;
  }
  if (normalized === "assigned" || normalized === "from_package" || name) {
    return "from_package";
  }
  if (normalized && normalized !== "not_assigned") {
    return normalized;
  }
  return "not_assigned";
}

export function documentProgressBadge({
  actionRequiredCount = 0,
  completeCount = 0,
  passengerCount = 0,
} = {}) {
  const actionRequired = Number(actionRequiredCount) || 0;
  if (actionRequired > 0) {
    return {
      value: "action_required",
      label: actionRequired === 1 ? "1 action required" : `${actionRequired} action required`,
    };
  }

  const complete = Number(completeCount) || 0;
  const passengers = Number(passengerCount) || 0;
  if (complete > 0 && (passengers === 0 || complete >= passengers)) {
    return { value: "complete", label: "Complete" };
  }

  return { value: "pending", label: "Pending" };
}

export function isCancelledBooking(value) {
  return ["cancelled", "canceled"].includes(normalizeBookingStatus(value));
}

export function isClosedForCollection(bookingStatus, paymentStatus) {
  if (isCancelledBooking(bookingStatus)) {
    return true;
  }
  const pay = normalizeBookingStatus(paymentStatus);
  return pay === "refunded" || pay === "refund_pending";
}

export function isPaidPayment(value) {
  return ["paid", "received", "approved"].includes(normalizeBookingStatus(value));
}

export function canUploadPaymentProof(bookingStatus, paymentStatus) {
  if (isClosedForCollection(bookingStatus, paymentStatus)) {
    return false;
  }
  return !isPaidPayment(paymentStatus);
}

export function isDraftBooking(value) {
  return normalizeBookingStatus(value) === "draft";
}

export function isAgentCancellable(value) {
  return AGENT_CANCELLABLE.includes(normalizeBookingStatus(value));
}

export function canEditPassengers(value) {
  return AGENT_EDITABLE_PASSENGERS.includes(normalizeBookingStatus(value));
}

export function isConfirmedBooking(value) {
  return CONFIRMED_STATUSES.includes(normalizeBookingStatus(value));
}

export function canRequestBookingChange(value) {
  return isConfirmedBooking(value);
}

export function formatPaymentStatus(value) {
  const normalized = normalizeBookingStatus(value);
  if (PAYMENT_STATUS_LABELS[normalized]) {
    return PAYMENT_STATUS_LABELS[normalized];
  }
  if (!normalized) {
    return "Payment pending";
  }
  return normalized.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}
